import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from "react-router-dom";
import { getAllSclasses, getSubjectList } from '../../../redux/sclassRelated/sclassHandle';
import { deleteUser } from '../../../redux/userRelated/userHandle';
import PostAddIcon from '@mui/icons-material/PostAdd';
import {
    Paper, Box, IconButton, Grid, Card, CardContent,
    Typography, Button, Chip, Breadcrumbs, Link, CircularProgress,
    Divider, Tooltip
} from '@mui/material';
import styled, { keyframes } from 'styled-components';
import DeleteIcon from "@mui/icons-material/Delete";
import TableTemplate from '../../../components/TableTemplate';
import { BlueButton, GreenButton } from '../../../components/buttonStyles';
import SpeedDialTemplate from '../../../components/SpeedDialTemplate';
import { useToast } from '../../../context/ToastContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookIcon from '@mui/icons-material/Book';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SchoolIcon from '@mui/icons-material/School';
import MenuBookIcon from '@mui/icons-material/MenuBook';

// Animation keyframes
const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const ShowSubjects = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { subjectsList, loading, error, response, getresponse, sclassesList } = useSelector((state) => state.sclass);
    const { currentUser } = useSelector(state => state.user);

    const [viewMode, setViewMode] = useState('classes'); // 'classes', 'subjects'
    const [selectedClass, setSelectedClass] = useState(null);
    const { showToast } = useToast();

    // Auto-select class when returning from ViewSubject
    useEffect(() => {
        const returnClassId = location.state?.returnToClass;
        if (returnClassId && sclassesList?.length > 0) {
            const cls = sclassesList.find(c => c._id === returnClassId);
            if (cls) {
                setSelectedClass(cls);
                setViewMode('subjects');
                dispatch(getSubjectList(cls._id, "ClassSubjects"));
            }
        }
    }, [sclassesList]);

    useEffect(() => {
        if (viewMode === 'classes') {
            dispatch(getAllSclasses(currentUser._id, "Sclass"));
        }
    }, [currentUser._id, dispatch, viewMode]);

    const handleClassClick = (classData) => {
        setSelectedClass(classData);
        setViewMode('subjects');
        dispatch(getSubjectList(classData._id, "ClassSubjects"));
    };

    const handleBack = () => {
        setViewMode('classes');
        setSelectedClass(null);
    };

    const deleteSubjectHandler = (deleteID, address) => {
        dispatch({ type: 'sclass/getFailed', payload: null });
        dispatch(deleteUser(deleteID, address))
            .then(() => {
                dispatch({ type: 'sclass/getSubjectsSuccess', payload: [] });
                dispatch(getSubjectList(selectedClass._id, "ClassSubjects"));
                showToast("Subject deleted successfully", "success");
            });
    };

    const subjectColumns = [
        { id: 'subName', label: 'Subject Name', minWidth: 170 },
        { id: 'subCode', label: 'Subject Code', minWidth: 100 },
        { id: 'sessions', label: 'Sessions', minWidth: 100 },
        { id: 'teacher', label: 'Teacher', minWidth: 170 },
    ];

    const subjectRows = subjectsList && subjectsList.map((subject) => ({
        subName: subject.subName,
        subCode: subject.subCode || 'N/A',
        sessions: subject.sessions,
        teacher: subject.teacher?.name || 'Not Assigned',
        id: subject._id,
    }));

    const SubjectsButtonHaver = ({ row }) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Delete">
                <IconButton onClick={() => deleteSubjectHandler(row.id, "Subject")}>
                    <DeleteIcon color="error" />
                </IconButton>
            </Tooltip>
        
        </Box>
    );
 

    const renderClassesView = () => (
        <Box>
            <HeaderSection>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1 }}>
                        Subject Management 📚
                    </Typography>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link color="inherit" href="/Admin/dashboard">Dashboard</Link>
                        <Typography color="text.primary">Subjects</Typography>
                    </Breadcrumbs>
                </Box>
            </HeaderSection>

            {loading ? (
                <LoadingWrapper>
                    <CircularProgress size={60} thickness={4} />
                    <Typography sx={{ mt: 2, fontWeight: 500 }}>Loading classes...</Typography>
                </LoadingWrapper>
            ) : (getresponse || response) ? (
                <EmptyState>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>No Classes Found</Typography>
                    <Typography color="textSecondary" sx={{ mb: 3 }}>
                        Add classes first to manage subjects.
                    </Typography>
                    <Button variant="outlined" onClick={() => navigate("/Admin/addclass")}>Add Class</Button>
                </EmptyState>
            ) : (
                <Grid container spacing={3}>
                    {Array.isArray(sclassesList) && sclassesList.map((cls, index) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={cls._id}>
                            <StyledClassCard onClick={() => handleClassClick(cls)} $delay={index * 0.1}>
                                <CardHeader $color={index % 2 === 0 ? '#667eea' : '#764ba2'}>
                                    <MenuBookIcon sx={{ color: 'white' }} />
                                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                                        Class {cls.sclassName}
                                    </Typography>
                                </CardHeader>
                                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Click to manage subjects for this class
                                    </Typography>
                                    <Button 
                                        variant="outlined" 
                                        endIcon={<ArrowForwardIcon />}
                                        sx={{ borderRadius: '10px' }}
                                    >
                                        View Subjects
                                    </Button>
                                </CardContent>
                            </StyledClassCard>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );

    const renderSubjectsView = () => (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={handleBack} sx={{ bgcolor: 'white', boxShadow: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                            Class {selectedClass?.sclassName} Subjects 📖
                        </Typography>
                        <Breadcrumbs aria-label="breadcrumb">
                            <Link color="inherit" href="#" onClick={handleBack}>Subjects</Link>
                            <Typography color="text.primary">{selectedClass?.sclassName}</Typography>
                        </Breadcrumbs>
                    </Box>
                </Box>
                <Button 
                    variant="contained" 
                    startIcon={<AddCircleIcon />}
                    onClick={() => navigate(`/Admin/addsubject/${selectedClass._id}`)}
                    sx={{ 
                        borderRadius: '12px',
                        padding: '10px 24px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                        }
                    }}
                >
                    Add Subject
                </Button>
            </Box>

            {loading ? (
                <LoadingWrapper>
                    <CircularProgress size={60} thickness={4} />
                    <Typography sx={{ mt: 2, fontWeight: 500 }}>Loading subjects...</Typography>
                </LoadingWrapper>
            ) : (getresponse || response) ? (
                <EmptyState>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>No Subjects Found</Typography>
                    <Typography color="textSecondary" sx={{ mb: 3 }}>
                        Start by adding subjects to this class.
                    </Typography>
                    <GreenButton variant="contained" onClick={() => navigate(`/Admin/addsubject/${selectedClass._id}`)}>
                        Add Subject
                    </GreenButton>
                </EmptyState>
            ) : (
                <Paper sx={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <TableTemplate buttonHaver={SubjectsButtonHaver} columns={subjectColumns} rows={subjectRows} />
                 </Paper>
            )}
        </Box>
    );

    return (
        <PageContainer>
            <MainContent>
                {viewMode === 'classes' ? renderClassesView() : renderSubjectsView()}
            </MainContent>
        </PageContainer>
    );
};

export default ShowSubjects;

// Styled Components
const PageContainer = styled(Box)`
  background-color: #f8f9fa;
  min-height: 100vh;
  padding: 24px;
`;

const MainContent = styled(Box)`
  max-width: 1400px;
  margin: 0 auto;
  animation: ${fadeIn} 0.6s ease-out;
`;

const HeaderSection = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 32px;
`;

const LoadingWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
`;

const EmptyState = styled(Paper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  border-radius: 20px;
  text-align: center;
  background: white;
  border: 1px dashed #ccc;
`;

const StyledClassCard = styled(Card)`
  border-radius: 20px !important;
  overflow: hidden !important;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08) !important;
  transition: all 0.3s ease !important;
  cursor: pointer;
  height: 100%;
  animation: ${fadeIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.$delay}s;
  opacity: 0;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 30px rgba(0,0,0,0.12) !important;
  }
`;

const CardHeader = styled(Box)`
  padding: 20px;
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  gap: 12px;
`;
